import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "@env";
import { PageManager } from "@shared/models/page-manager";
import { Pagination } from "@shared/models/pagination";
import { VehicleModelDTO } from "@shared/models/vehicle-model-dto";
import { LanguageService } from "./language.service";
import { SearchFormDTO } from "../../modules/private/modules/vehicle-model/components/models/search-form-dto";
import { VehicleModelCategoriesDTO } from "@shared/models/vehicle-model-categories-dto";
import { SearchFormPartsDTO } from "@shared/widgets/vehicle-model-auto-complete-parts/models/search-form-dto";

@Injectable({
  providedIn: "root"
})
export class VehicleModelService {
  private vehicleModelUrl = "models";

  constructor(
    private http: HttpClient,
    private languageService: LanguageService
  ) {}

  save(vehicleModelDTO: VehicleModelDTO): Observable<any> {
    return this.http.post<any>(
      environment.apiUrl + this.vehicleModelUrl,
      vehicleModelDTO
    );
  }

  listAll() {
    return this.languageService.getLanguage$().pipe(
      map((currentLanguage) => {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Accept-Language": currentLanguage
        });
        return this.http
          .get<VehicleModelDTO[]>(environment.apiUrl + this.vehicleModelUrl, {
            headers
          })
          .pipe(map((service) => service));
      })
    );
  }

  delete(id: number): Observable<any> {
    return this.http.delete<any>(
      environment.apiUrl + `${this.vehicleModelUrl}/${id}`
    );
  }

  findById(id: number): Observable<VehicleModelDTO> {
    return this.http.get<VehicleModelDTO>(
      environment.apiUrl + `${this.vehicleModelUrl}/${id}`
    );
  }

  edit(id: number, vehicleModelDTO: VehicleModelDTO): Observable<any> {
    return this.http.put<any>(
      environment.apiUrl + `${this.vehicleModelUrl}/${id}`,
      vehicleModelDTO
    );
  }

  getModelCategories() {
    return this.http.get<VehicleModelCategoriesDTO[]>(
      environment.apiUrl + `${this.vehicleModelUrl}/categories`
    );
  }

  getModelCategoriesCarWorkshop() {
    return this.http.get<any[]>(environment.apiUrl + "model-categories");
  }

  findByBrandId(brandId: number): Observable<VehicleModelDTO[]> {
    return this.http.get<VehicleModelDTO[]>(
      environment.apiUrl + `${this.vehicleModelUrl}/filters?brandId=${brandId}`
    );
  }

  filterModels(params): Observable<VehicleModelDTO[]> {
    return this.http.get<VehicleModelDTO[]>(
      environment.apiUrl + `${this.vehicleModelUrl}/search`,
      { params: params }
    );
  }


  findByFilters(
    searchFormDTO: SearchFormDTO,
    pageManager?: PageManager
  ): Observable<Pagination<VehicleModelDTO[]>> {
    const queryParams: string[] = [];

    if (pageManager != null) {
      queryParams.push(`page=${pageManager.pageIndex}`);
      queryParams.push(`size=${pageManager.pageSize}`);
    }

    if (searchFormDTO.brandId) {
      queryParams.push(`brandId=${searchFormDTO.brandId}`);
    }
    if (searchFormDTO.name) {
      queryParams.push(`name=${searchFormDTO.name}`);
    }
    if (searchFormDTO.motor) {
      queryParams.push(`motor=${searchFormDTO.motor}`);
    }
    if (searchFormDTO.version) {
      queryParams.push(`version=${searchFormDTO.version}`);
    }
    if (searchFormDTO.year) {
      queryParams.push(`year=${searchFormDTO.year}`);
    }
    if (searchFormDTO.haveMaintenancePlan != null) {
      queryParams.push(
        `haveMaintenancePlan=${searchFormDTO.haveMaintenancePlan}`
      );
    }
    if (searchFormDTO.havePriceTable != null) {
      queryParams.push(`havePriceTable=${searchFormDTO.havePriceTable}`);
    }

    const query = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

    if (pageManager != null) {
      return this.http.get<Pagination<VehicleModelDTO[]>>(
        environment.apiUrl + `${this.vehicleModelUrl}/filters${query}`
      );
    }

    return this.http.get<Pagination<VehicleModelDTO[]>>(
      environment.apiUrl + `${this.vehicleModelUrl}/filters${query}`
    );
  }

  findByFiltersParts(searchFormDTO: SearchFormPartsDTO): Observable<any> {
    let query = "?";
    if (searchFormDTO.brandName) {
      query += `brand=${searchFormDTO.brandName}`;
    }
    if (searchFormDTO.name) {
      query += `&description=${searchFormDTO.name}`;
    }
    query += `&active=true`;
    return this.http.get<any>(environment.microserviceParts + `model${query}`);
  }
}
